import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '../hooks';
import { queries } from '../queries';
import type { AuditEvent } from '../api';
import { Checkbox, FilterChips, Page, PageError, PageHeader, PageLoading } from '../components/primitives';
import { ResponsiveTable } from '../components/primitives/ResponsiveTable';
import type { Column } from '../components/primitives/ResponsiveTable';
import { Timestamp } from '../components/status/StatusPieces';
import {
  auditActionLabel,
  auditArea,
  auditAreaLabel,
  auditDetails,
  auditSummary,
  isTechnicalDetail,
  type AuditArea,
  type AuditDetail,
} from '../components/auditLog';
import { relativeTime } from '../components/project/projectHealth';

/** What Commerce Ops did on its own: retention, schedules, key rotation. */
export function isAutomaticActor(actor: string): boolean {
  return /^(system|scheduler|worker)\b/i.test(actor.trim());
}

/** "system: backup schedule" → "Commerce Ops (backup schedule)". */
export function actorLabel(actor: string): string {
  const trimmed = actor.trim();
  const system = /^system\s*:?\s*(.*)$/i.exec(trimmed);
  if (system) return system[1] ? `Commerce Ops (${system[1]})` : 'Commerce Ops';
  if (/^(scheduler|worker)$/i.test(trimmed)) return `Commerce Ops (${trimmed.toLowerCase()})`;
  return trimmed;
}

/** One row: an event, or the same event repeated back to back. */
export interface ActivityGroup {
  key: string;
  latest: AuditEvent;
  events: AuditEvent[];
}

/**
 * groupRepeats folds consecutive events that are the same action by the same
 * actor on the same project into one row ("ERP issue ignored, 4 times"); the
 * order of everything else is kept.
 */
export function groupRepeats(events: AuditEvent[]): ActivityGroup[] {
  const groups: ActivityGroup[] = [];
  for (const event of events) {
    const last = groups[groups.length - 1];
    if (last && last.latest.action === event.action && last.latest.actor === event.actor && (last.latest.project_id ?? '') === (event.project_id ?? '')) {
      last.events.push(event);
    } else {
      groups.push({ key: event.id, latest: event, events: [event] });
    }
  }
  return groups;
}

function DetailValue({ detail }: { detail: AuditDetail }) {
  return detail.href ? (
    <Link to={detail.href} title={detail.full}>
      {detail.text}
    </Link>
  ) : (
    <span title={detail.full}>{detail.text}</span>
  );
}

function EventCell({ group }: { group: ActivityGroup }) {
  const event = group.latest;
  const summary = auditSummary(event.action, event.metadata);
  const details = auditDetails(event.metadata).filter((d) => d.key !== 'project_id' && !summary?.keys.includes(d.key));
  const facts = details.filter((d) => !isTechnicalDetail(d.key, event.metadata?.[d.key]));
  const technical = details.filter((d) => isTechnicalDetail(d.key, event.metadata?.[d.key]));
  const repeats = group.events.length;
  const oldest = group.events[repeats - 1]!;
  return (
    <span className="cell-stack activity-event">
      <span className="cell-stack__primary">{auditActionLabel(event.action)}</span>
      {summary ? <span className="activity-event__summary">{summary.text}</span> : null}
      {repeats > 1 ? (
        <span className="cell-stack__secondary" data-testid="activity-repeats">
          {repeats} times · first {relativeTime(oldest.occurred_at)}
        </span>
      ) : null}
      {facts.length > 0 ? (
        <ul className="audit-details">
          {facts.map((d) => (
            <li key={d.key}>
              <span className="audit-details__key">{d.label}</span> <DetailValue detail={d} />
            </li>
          ))}
        </ul>
      ) : null}
      {technical.length > 0 ? (
        <details className="quiet-disclosure activity-event__technical" data-testid="activity-technical">
          <summary>Technical details</summary>
          <ul className="audit-details">
            {technical.map((d) => (
              <li key={d.key}>
                <span className="audit-details__key">{d.label}</span> <DetailValue detail={d} />
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </span>
  );
}

/**
 * Activity: who did what, newest first (CO-87). Each event reads as its name
 * and one line in words; routes, statuses and identifiers open on request
 * (CO-161). The same event repeated back to back is one row, and what
 * Commerce Ops did on its own can be left out.
 */
export function AuditLogPage() {
  const [area, setArea] = useState<AuditArea | 'all'>('all');
  const [showAutomatic, setShowAutomatic] = useState(true);
  const events = useQuery(queries.auditEvents());
  const projects = useQuery(queries.projects());

  const projectName = useMemo(() => {
    const byId = new Map((projects.data?.projects ?? []).map((p) => [p.id, p.name]));
    return (id?: string) => (id ? byId.get(id) : undefined);
  }, [projects.data]);

  const all = events.data?.events ?? [];
  const counts = useMemo(() => {
    const c = new Map<AuditArea, number>();
    for (const e of all) c.set(auditArea(e.action), (c.get(auditArea(e.action)) ?? 0) + 1);
    return c;
  }, [all]);
  const automaticCount = useMemo(() => all.filter((e) => isAutomaticActor(e.actor)).length, [all]);

  // Loading and a failed read keep the page's header, and a failure can be tried again (CO-184, OR3).
  if (events.loading) return <PageLoading title="Activity" description="Secret values are never recorded." rows={6} />;
  if (events.error) return <PageError title="Activity" description="Secret values are never recorded." error={events.error} onRetry={events.reload} />;

  const inArea = area === 'all' ? all : all.filter((e) => auditArea(e.action) === area);
  const shown = showAutomatic ? inArea : inArea.filter((e) => !isAutomaticActor(e.actor));
  const groups = groupRepeats(shown);
  const areas = (Object.keys(auditAreaLabel) as AuditArea[]).filter((a) => counts.has(a));

  const columns: Column<ActivityGroup>[] = [
    {
      id: 'when',
      header: 'When',
      // On a phone the time sits at the card's top right.
      card: 'status',
      width: 'time',
      cell: (group) => <Timestamp iso={group.latest.occurred_at} />,
    },
    {
      id: 'event',
      header: 'Event',
      card: 'title',
      width: 'fill',
      cell: (group) => <EventCell group={group} />,
    },
    {
      id: 'project',
      header: 'Project',
      width: 16,
      cell: (group) =>
        group.latest.project_id ? (
          <Link to={`/app/projects/${group.latest.project_id}`}>{projectName(group.latest.project_id) ?? group.latest.project_id.slice(0, 8)}</Link>
        ) : (
          <span className="muted">No project</span>
        ),
    },
    {
      id: 'actor',
      header: 'By',
      width: 20,
      cell: (group) => <span className={isAutomaticActor(group.latest.actor) ? 'muted' : undefined}>{actorLabel(group.latest.actor)}</span>,
    },
  ];

  return (
    <Page>
      <PageHeader title="Activity" description="Secret values are never recorded." />

      {all.length > 0 ? (
        <div className="activity-filters">
          <FilterChips
            label="Show activity about"
            value={area}
            onChange={setArea}
            options={[
              { value: 'all', label: 'All', count: all.length },
              ...areas.map((a) => ({ value: a, label: auditAreaLabel[a], count: counts.get(a) })),
            ]}
          />
          {automaticCount > 0 ? (
            <Checkbox
              checked={showAutomatic}
              onChange={(event) => setShowAutomatic(event.target.checked)}
              className="activity-filters__automatic"
              label={<>Include automated actions ({automaticCount})</>}
            />
          ) : null}
        </div>
      ) : null}

      {all.length === 0 ? (
        <p className="quiet-empty">No activity</p>
      ) : groups.length === 0 ? (
        <p className="quiet-empty" data-testid="activity-none-shown">
          No activity by people
        </p>
      ) : (
        <ResponsiveTable
          rows={groups}
          rowKey={(group) => group.key}
          caption="Activity, newest first"
          className="activity-table"
          testId="activity-table"
          columns={columns}
        />
      )}
    </Page>
  );
}
